import { collection, doc, query, setDoc, where } from "firebase/firestore";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, FieldLabel, LoadingView, PrimaryButton, SBCard, SecondaryButton } from "@/components/ui";
import { db } from "@/firebase/app";
import { useCollection } from "@/hooks/firestoreHooks";
import {
  competitionTemplateFirestoreData,
  parseCompetitionTemplate,
  PARTICIPANT_FORMATS,
  RESULT_FORMATS,
  type LeagueCompetitionParticipantFormat,
  type LeagueCompetitionResultFormat,
  type LeagueCompetitionTemplate,
} from "@/models/competition";
import {
  makeFieldForRole,
  sbotSlug,
  UNIVERSAL_FIELD_ROLES,
  UNIVERSAL_ROLE_LABELS,
  type LeagueCompetitionUniversalFieldRole,
} from "@/models/fieldDefinition";
import { activeMembership } from "@/models/user";
import { humanActionAuditActor, recordActionAudit } from "@/services/actionAudit";
import { useActiveLeagueID } from "@/features/home/useLeagueSnapshot";
import "@/features/home/home.css";
import "@/features/league/league.css";

// Port of LeagueCompetitionConfigView: manage competition templates for the
// active league — create, add universal-role fields, archive.

const GPS_STARTER_ROLES: LeagueCompetitionUniversalFieldRole[] = [
  "eventName",
  "eventCode",
  "eventStartDateTime",
  "eventVenueName",
  "eventCity",
  "eventLatitude",
  "eventLongitude",
  "eventTimeZone",
  "gpsRace",
  "gpsWindowStart",
  "gpsWindowEnd",
  "courseKML",
];

export function TemplatesConfig() {
  const { currentUser } = useAuth();
  const { leagueID } = useActiveLeagueID();
  const sportID = currentUser ? activeMembership(currentUser)?.sportID : undefined;

  const templatesQuery = leagueID ? query(collection(db, "leagueCompetitionTemplates"), where("leagueID", "==", leagueID)) : null;
  const templates = useCollection(templatesQuery, parseCompetitionTemplate, `templates:${leagueID}`);

  const [name, setName] = useState("");
  const [participantFormat, setParticipantFormat] = useState<LeagueCompetitionParticipantFormat>("individual");
  const [resultFormat, setResultFormat] = useState<LeagueCompetitionResultFormat>("placement");
  const [includeGPSStarter, setIncludeGPSStarter] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [expandedID, setExpandedID] = useState<string | null>(null);
  const [roleToAdd, setRoleToAdd] = useState<LeagueCompetitionUniversalFieldRole>("eventName");

  const sortedTemplates = useMemo(
    () => [...templates.data].sort((a, b) => Number(a.isArchived) - Number(b.isArchived) || a.name.localeCompare(b.name)),
    [templates.data],
  );

  const saveTemplate = async (
    template: LeagueCompetitionTemplate,
    actionType: "create" | "update" | "archive" = "update",
    original?: LeagueCompetitionTemplate,
  ) => {
    const timestamp = new Date();
    const next: LeagueCompetitionTemplate = {
      ...template,
      version: template.version + 1,
      createdAt: template.createdAt ?? timestamp,
      updatedAt: timestamp,
      updatedByUserID: currentUser?.id,
      createdByUserID: template.createdByUserID ?? currentUser?.id,
    };
    await setDoc(doc(db, "leagueCompetitionTemplates", next.id), competitionTemplateFirestoreData(next), { merge: true });
    if (currentUser) {
      void recordActionAudit({
        before: original ? competitionTemplateFirestoreData(original) : undefined,
        after: competitionTemplateFirestoreData(next),
        context: {
          leagueID: next.leagueID,
          targetCollection: "leagueCompetitionTemplates",
          targetDocumentID: next.id,
          targetDisplayName: next.name,
          actionType,
          actor: humanActionAuditActor(currentUser),
        },
      });
    }
  };

  const createTemplate = async () => {
    if (!leagueID || !sportID || !name.trim()) {
      setError(!sportID ? "Your active workspace has no sport — switch workspaces." : "Template name is required.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const slug = sbotSlug(name);
      const fields = includeGPSStarter
        ? GPS_STARTER_ROLES.map((role, index) => makeFieldForRole(role, index, new Set<string>()))
        : [];
      const template: LeagueCompetitionTemplate = {
        id: `${leagueID}_${slug}`,
        leagueID,
        sportID,
        name: name.trim(),
        slug,
        visibility: "private",
        participantFormat,
        resultFormat,
        version: 0,
        fields,
        isArchived: false,
        createdByUserID: currentUser?.id,
        createdAt: new Date(),
      };
      await saveTemplate(template, "create");
      setName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Template could not be created.");
    } finally {
      setIsSaving(false);
    }
  };

  const addRoleField = async (template: LeagueCompetitionTemplate) => {
    try {
      const existing = new Set(template.fields.map((field) => field.id));
      const sortOrder = template.fields.length ? Math.max(...template.fields.map((f) => f.sortOrder)) + 1 : 0;
      const field = makeFieldForRole(roleToAdd, sortOrder, existing);
      await saveTemplate({ ...template, fields: [...template.fields, field] }, "update", template);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Field could not be added.");
    }
  };

  const toggleArchive = async (template: LeagueCompetitionTemplate) => {
    try {
      const isArchiving = !template.isArchived;
      await saveTemplate({ ...template, isArchived: isArchiving }, isArchiving ? "archive" : "update", template);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Template could not be updated.");
    }
  };

  return (
    <div className="list-screen league-screen">
      <div className="list-screen-head">
        <h1>Competitions</h1>
        <Link to="/league/competitions/new" className="badge badge-published league-navigation-action">
          New competition
        </Link>
      </div>

      <SBCard className="home-card">
        <h3>Create a template</h3>
        <FieldLabel label="Template name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Endurance Race" />
        </FieldLabel>
        <div className="league-form-grid">
          <FieldLabel label="Participants">
            <select value={participantFormat} onChange={(e) => setParticipantFormat(e.target.value as LeagueCompetitionParticipantFormat)}>
              {PARTICIPANT_FORMATS.map((format) => (
                <option key={format} value={format}>
                  {format}
                </option>
              ))}
            </select>
          </FieldLabel>
          <FieldLabel label="Result format">
            <select value={resultFormat} onChange={(e) => setResultFormat(e.target.value as LeagueCompetitionResultFormat)}>
              {RESULT_FORMATS.map((format) => (
                <option key={format} value={format}>
                  {format}
                </option>
              ))}
            </select>
          </FieldLabel>
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
          <input
            type="checkbox"
            checked={includeGPSStarter}
            onChange={(e) => setIncludeGPSStarter(e.target.checked)}
            style={{ width: "auto" }}
          />
          Start with GPS event fields (name, code, schedule, venue, GPS race, course KML)
        </label>
        <PrimaryButton title={isSaving ? "Creating…" : "Create template"} onClick={() => void createTemplate()} disabled={isSaving} />
        <ErrorText>{error}</ErrorText>
      </SBCard>

      <SBCard className="home-card">
        <h3>Templates</h3>
        <ErrorText>{templates.error}</ErrorText>
        {templates.loading && sortedTemplates.length === 0 ? (
          <LoadingView />
        ) : sortedTemplates.length === 0 ? (
          <p className="home-hint">No templates yet — create one above.</p>
        ) : (
          sortedTemplates.map((template) => (
            <div key={template.id} style={{ opacity: template.isArchived ? 0.55 : 1 }}>
              <div className="event-row league-management-row" style={{ cursor: "default" }}>
                <div className="event-row-main">
                  <strong>{template.name}</strong>
                  <span>
                    {template.participantFormat} · {template.resultFormat} · v{template.version} · {template.fields.filter((f) => !f.isArchived).length}{" "}
                    fields
                  </span>
                </div>
                <div className="event-row-badges league-row-actions">
                  <SecondaryButton
                    title={expandedID === template.id ? "Close" : "Fields"}
                    onClick={() => setExpandedID(expandedID === template.id ? null : template.id)}
                  />
                  <SecondaryButton title={template.isArchived ? "Restore" : "Archive"} destructive={!template.isArchived} onClick={() => void toggleArchive(template)} />
                </div>
              </div>
              {expandedID === template.id && (
                <div className="league-template-fields">
                  {[...template.fields]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((field) => (
                      <div key={field.id} className="home-hint">
                        {field.label} — {field.type}
                        {field.universalCompetitionRole ? ` (${UNIVERSAL_ROLE_LABELS[field.universalCompetitionRole]})` : ""}
                        {field.isArchived ? " · archived" : ""}
                      </div>
                    ))}
                  <div className="league-role-field">
                    <FieldLabel label="Universal field role"><select value={roleToAdd} onChange={(e) => setRoleToAdd(e.target.value as LeagueCompetitionUniversalFieldRole)}>
                      {UNIVERSAL_FIELD_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {UNIVERSAL_ROLE_LABELS[role]}
                        </option>
                      ))}
                    </select></FieldLabel>
                    <SecondaryButton title="Add role field" onClick={() => void addRoleField(template)} />
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </SBCard>
    </div>
  );
}
